import { Fragment, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { CaretDown, X } from "@phosphor-icons/react"
import { FormError } from "@/components/ui/form"
import { ScrollArea } from "@/components/ui/scroll-area"
import { SearchContent, SearchItem, SuggestionSearch } from "@/components/ui/search"
import { useClickAway } from "@/hooks/useClickAway"
import { removeDiacritic } from "@/lib/removeDiacritics"
import { cn } from "@/lib/utils"

export type SelectBoxOption = { readonly label: string; readonly value: string }

type SelectBoxWithSearchProps = {
  options: ReadonlyArray<SelectBoxOption>
  className?: string
  type?: string
  value: string
  placeholder: string
  required?: boolean
  error?: string
  showLabel?: boolean
  testId?: string
  ref?: React.Ref<HTMLInputElement>
  renderOption?: (option: SelectBoxOption, index: number) => React.ReactNode
  renderBorder?: (option: SelectBoxOption) => React.ReactNode
  onChange: (option?: SelectBoxOption) => void
  onSearchChange?: (value: string) => void
  ignoreLocalFilter?: boolean
}

export const SelectBoxWithSearch = (props: SelectBoxWithSearchProps) => {
  const {
    error,
    value,
    options,
    className,
    type,
    placeholder,
    showLabel,
    testId,
    ref,
    required,
    onChange,
    onSearchChange,
    renderOption,
    renderBorder,
    ignoreLocalFilter,
  } = props
  const { t } = useTranslation()
  const [keyword, setKeyword] = useState("")
  const [searchResultVisible, setSearchResultVisible] = useState(false)

  const searchResultsRef = useClickAway(() => setSearchResultVisible(false))

  useEffect(() => {
    if (value) setKeyword(showLabel ? (options.find((o) => o.value === value)?.label ?? "") : value)
  }, [value, showLabel, options])

  const handleClearSearch = () => {
    setKeyword("")
    onChange(undefined)
  }

  const filteredOptions = useMemo(
    () =>
      ignoreLocalFilter
        ? options
        : options.filter((o) =>
            removeDiacritic(o.label).toLowerCase().includes(removeDiacritic(keyword).toLowerCase()),
          ),
    [keyword, options, ignoreLocalFilter],
  )
  const finalTestId = testId ? `${testId}` : "search"

  return (
    <SuggestionSearch className={cn(className)}>
      <div className="relative">
        <label htmlFor={`${type}-input-search`} className="absolute top-1/2 right-1 -translate-y-1/2 p-2">
          <CaretDown className="text-on-surface-subdued" />
        </label>
        <input
          ref={ref}
          id={`${type}-input-search`}
          value={keyword}
          className={cn(
            "peer border-border bg-surface-subdued text-on-surface placeholder:text-on-surface-subdued hover:bg-surface-hovered focus:outline-primary active:bg-surface-pressed h-12 w-full rounded-md border pt-0 pr-23.75 pl-4 text-sm outline-offset-0 transition-all focus:outline-2 disabled:cursor-not-allowed",
            error && "outline-critical! outline-1 focus:outline-2",
          )}
          data-testid={`${finalTestId}-input`}
          autoComplete="off"
          aria-autocomplete="none"
          placeholder={`${placeholder}${required ? "*" : ""}`}
          onChange={({ target }) => {
            setKeyword(target.value)
            onSearchChange?.(target.value)
          }}
          onClick={() => setSearchResultVisible(true)}
        />
        {value && keyword && (
          <span
            className="hover:bg-surface-hovered visible absolute top-1/2 right-14 -translate-y-1/2 cursor-pointer rounded-full p-1 transition-all"
            onClick={handleClearSearch}
            data-testid={`clear-${finalTestId}-button`}>
            <X className="text-on-surface-subdued h-6 w-6 cursor-pointer" />
          </span>
        )}
      </div>
      {error && <FormError>{t(error)}</FormError>}
      {searchResultVisible && (
        <SearchContent ref={searchResultsRef}>
          {filteredOptions.length === 0 ? (
            <p className="text-on-surface-subdued cursor-default px-2 text-sm select-none">
              {t("common.nothingFound")}
            </p>
          ) : (
            <ScrollArea viewportClassName="max-h-64">
              <div className="space-y-0.75 py-2 text-left">
                {filteredOptions.map((option: SelectBoxOption, idx) => (
                  <Fragment key={`${option.value}-${idx + 1}`}>
                    <SearchItem
                      value={`${option.value}-${idx}`}
                      onSelect={() => {
                        onChange(option)
                        setKeyword(option.label)
                        setSearchResultVisible(false)
                      }}
                      className="text-on-surface"
                      data-testid={`${finalTestId}-item`}>
                      {renderOption ? renderOption(option, idx) : option.label}
                    </SearchItem>
                    {renderBorder?.(option)}
                  </Fragment>
                ))}
              </div>
            </ScrollArea>
          )}
        </SearchContent>
      )}
    </SuggestionSearch>
  )
}
